import { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useTranslation } from "react-i18next";
import config from "../../config";
import {
  PersonalNote,
  PersonalNoteTargetType,
} from "../../models/personalNote";
import { createPersonalNoteService } from "../../services/personalNoteService";
import ConfirmationPanel from "../confirmationPanel";

interface PersonalNotesPanelProps {
  targetType: PersonalNoteTargetType;
  targetId?: string | null;
  onNotesChange?: (notes: PersonalNote[]) => void;
  /** Passed from the parent Dialog's onEntered — keeps spinner visible until the animation finishes. */
  dialogEntered?: boolean;
}

const PersonalNotesPanel = ({
  targetType,
  targetId,
  onNotesChange,
  dialogEntered = true,
}: PersonalNotesPanelProps) => {
  const { t, i18n } = useTranslation();
  const personalNoteService = useMemo(
    () => createPersonalNoteService(config.api.baseUrl),
    [],
  );

  const [notes, setNotes] = useState<PersonalNote[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<PersonalNote | null>(null);
  const onNotesChangeRef = useRef(onNotesChange);

  // Track which (targetType + targetId) has already been fetched so we can
  // derive `loading=true` on the very first render, skipping the empty flash.
  const loadKey = targetId ? `${targetType}:${targetId}` : null;
  const loadedKeyRef = useRef<string | null>(null);

  // `loading` is true whenever a fetch is in-flight OR we know a fetch is
  // needed but hasn't started yet (the very first render after targetId is set),
  // OR the Dialog's enter animation hasn't finished (prevents content flash
  // when the fetch resolves faster than the ~300ms fade-in).
  const loading =
    !dialogEntered ||
    isFetching ||
    (loadKey !== null && loadedKeyRef.current !== loadKey);

  useEffect(() => {
    onNotesChangeRef.current = onNotesChange;
  }, [onNotesChange]);

  // Auto-dismiss error so it doesn't linger forever after a failed
  // load/save/delete/update. Suspend while a request is in-flight.
  useEffect(() => {
    if (!errorMessage || isFetching || saving) return;
    const timeoutId = window.setTimeout(() => setErrorMessage(""), 6000);
    return () => window.clearTimeout(timeoutId);
  }, [errorMessage, isFetching, saving]);

  useEffect(() => {
    if (!loadKey || loadedKeyRef.current === loadKey) return;

    let cancelled = false;

    const loadNotes = async () => {
      setIsFetching(true);
      setErrorMessage("");

      try {
        const loadedNotes = await personalNoteService.getNotesForTarget(
          targetType,
          targetId!,
        );

        if (cancelled) return;

        setNotes(loadedNotes);
        loadedKeyRef.current = loadKey;
        onNotesChangeRef.current?.(loadedNotes);
      } catch (error) {
        if (cancelled) return;

        console.error("Failed to load personal notes", error);
        setNotes([]);
        loadedKeyRef.current = loadKey;
        setErrorMessage(t("meetingNotes.personalNotes.errors.load"));
        onNotesChangeRef.current?.([]);
      } finally {
        if (!cancelled) setIsFetching(false);
      }
    };

    void loadNotes();

    return () => {
      cancelled = true;
    };
  }, [personalNoteService, t, loadKey, targetId, targetType]);

  const applyNotes = (nextNotes: PersonalNote[]) => {
    setNotes(nextNotes);
    onNotesChangeRef.current?.(nextNotes);
  };

  const handleAddNote = async () => {
    if (!targetId || !newNoteContent.trim()) {
      return;
    }

    setSaving(true);
    setErrorMessage("");

    try {
      const savedNote = await personalNoteService.createNote({
        targetType,
        targetId,
        content: newNoteContent.trim(),
      });
      const updatedNotes = [savedNote, ...notes];
      applyNotes(updatedNotes);
      setNewNoteContent("");
      setIsAdding(false);
    } catch (error) {
      console.error("Failed to create personal note", error);
      setErrorMessage(t("meetingNotes.personalNotes.errors.save"));
    } finally {
      setSaving(false);
    }
  };

  const showDeleteConfirmation = (note: PersonalNote) => {
    setNoteToDelete(note);
    setDeleteConfirmOpen(true);
  };

  const hideDeleteConfirmation = () => {
    setDeleteConfirmOpen(false);
    setNoteToDelete(null);
  };

  const confirmDeleteNote = async () => {
    if (!noteToDelete?.id) return;

    setSaving(true);
    setErrorMessage("");

    try {
      await personalNoteService.deleteNote(noteToDelete.id);
      applyNotes(notes.filter((note) => note.id !== noteToDelete.id));
    } catch (error) {
      console.error("Failed to delete personal note", error);
      setErrorMessage(t("meetingNotes.personalNotes.errors.delete"));
    } finally {
      setSaving(false);
      hideDeleteConfirmation();
    }
  };

  const handleStartEdit = (note: PersonalNote) => {
    setEditingNoteId(note.id || null);
    setEditContent(note.content);
  };

  const handleSaveEdit = async (noteId: string) => {
    if (!editContent.trim()) {
      return;
    }

    const existingNote = notes.find((note) => note.id === noteId);
    if (!existingNote) {
      return;
    }

    setSaving(true);
    setErrorMessage("");

    try {
      const savedNote = await personalNoteService.updateNote({
        ...existingNote,
        content: editContent.trim(),
      });

      applyNotes(notes.map((note) => (note.id === noteId ? savedNote : note)));
      setEditingNoteId(null);
      setEditContent("");
    } catch (error) {
      console.error("Failed to update personal note", error);
      setErrorMessage(t("meetingNotes.personalNotes.errors.update"));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditContent("");
  };

  const formatDate = (date?: Date) => {
    if (!date) {
      return "";
    }

    const resolvedDate = new Date(date);
    return resolvedDate.toLocaleString(i18n.language || undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LockOutlinedIcon sx={{ fontSize: 16, color: "#64748B" }} />
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "#64748B",
            }}
          >
            {t("meetingNotes.personalNotes.sectionTitle")}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.6875rem",
              color: "#9CA3AF",
              fontStyle: "italic",
            }}
          >
            {t("meetingNotes.personalNotes.visibleOnlyToYou")}
          </Typography>
        </Box>
        {!isAdding && !!targetId && (
          <Button
            size="small"
            startIcon={<AddOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={() => setIsAdding(true)}
            disabled={saving}
            sx={{
              fontSize: "0.75rem",
              textTransform: "none",
              color: "#242C63",
              "&:hover": { backgroundColor: "#F1F5F9" },
            }}
          >
            {t("meetingNotes.personalNotes.addNote")}
          </Button>
        )}
      </Box>

      {!targetId ? (
        <Typography
          sx={{ fontSize: "0.8125rem", color: "#9CA3AF", fontStyle: "italic" }}
        >
          {t("meetingNotes.personalNotes.saveItemFirst")}
        </Typography>
      ) : (
        <>
          {errorMessage ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          ) : null}

          {isAdding && (
            <Box sx={{ mb: 2, backgroundColor: "#FFF", borderRadius: "6px" }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                placeholder={t(
                  "meetingNotes.personalNotes.placeholders.newNote",
                )}
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                autoFocus
                sx={{
                  mb: 1.5,
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.875rem",
                    backgroundColor: "#FFFFFF",
                    "& fieldset": { borderColor: "#E5E7EB" },
                    "&:hover fieldset": { borderColor: "#242C63" },
                    "&.Mui-focused fieldset": { borderColor: "#242C63" },
                  },
                }}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setIsAdding(false);
                    setNewNoteContent("");
                  }}
                  sx={{
                    fontSize: "0.75rem",
                    textTransform: "none",
                    color: "#64748B",
                  }}
                >
                  {t("button.cancel")}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleAddNote}
                  disabled={!newNoteContent.trim() || saving}
                  sx={{
                    fontSize: "0.75rem",
                    textTransform: "none",
                    backgroundColor: "#242C63",
                    "&:hover": { backgroundColor: "#353D7A" },
                    "&.Mui-disabled": { backgroundColor: "#D1D5DB" },
                  }}
                >
                  {t("button.save")}
                </Button>
              </Box>
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: "0.8125rem", color: "#64748B" }}>
                {t("meetingNotes.personalNotes.loading")}
              </Typography>
            </Box>
          ) : notes.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {notes.map((note) => (
                <Box
                  key={note.id}
                  sx={{
                    p: 1.5,
                    backgroundColor: "#FFF8E6",
                    borderRadius: "6px",
                    border: "1px solid #F2D48A",
                  }}
                >
                  {editingNoteId === note.id ? (
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={6}
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        autoFocus
                        sx={{
                          mb: 1,
                          "& .MuiOutlinedInput-root": {
                            fontSize: "0.8125rem",
                            backgroundColor: "#FFFFFF",
                            "& fieldset": { borderColor: "#927a1a" },
                            "&:hover fieldset": { borderColor: "#F59E0B" },
                            "&.Mui-focused fieldset": {
                              borderColor: "#F59E0B",
                            },
                          },
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 0.5,
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleCancelEdit}
                          sx={{
                            color: "#64748B",
                            textTransform: "none",
                            fontSize: "0.8125rem",
                            minWidth: "auto",
                            padding: "4px 8px",
                          }}
                        >
                          {t("button.cancel")}
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleSaveEdit(note.id || "")}
                          disabled={!editContent.trim() || saving}
                          sx={{
                            backgroundColor: "#242C63",
                            textTransform: "none",
                            fontSize: "0.8125rem",
                            minWidth: "auto",
                            padding: "4px 8px",
                            "&:hover": {
                              backgroundColor: "#1a2047",
                            },
                          }}
                        >
                          {t("button.save")}
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: "0.8125rem",
                            color: "#1A2048",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {note.content}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.6875rem",
                            color: "#9CA3AF",
                            mt: 0.5,
                          }}
                        >
                          {formatDate(note.updatedDate || note.createdDate)}
                          {note.updatedDate
                            ? t("meetingNotes.personalNotes.editedSuffix")
                            : ""}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.25 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleStartEdit(note)}
                          disabled={saving}
                          sx={{
                            color: "#64748B",
                            padding: 0.5,
                            "&:hover": { color: "#242C63" },
                          }}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => showDeleteConfirmation(note)}
                          disabled={saving}
                          sx={{
                            color: "#64748B",
                            padding: 0.5,
                            "&:hover": { color: "#EF4444" },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            !isAdding && (
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  color: "#9CA3AF",
                  fontStyle: "italic",
                }}
              >
                {t("meetingNotes.personalNotes.empty")}
              </Typography>
            )
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationPanel
        showModal={deleteConfirmOpen}
        hideModal={hideDeleteConfirmation}
        confirmModal={confirmDeleteNote}
        entry={noteToDelete}
        type="note"
        action="delete"
      />
    </Box>
  );
};

export default PersonalNotesPanel;
